import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase/firebaseConfig';
import { GeneralSettings } from '../types/settings';

const SETTINGS_COLLECTION = 'settings';
const GENERAL_DOC = 'general';

export class SettingsService {
  static async getGeneralSettings(): Promise<GeneralSettings> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, GENERAL_DOC);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as GeneralSettings;
      } else {
        const defaultSettings: GeneralSettings = {
          intro_video: '',
          default_coach: '',
        };
        return defaultSettings;
      }
    } catch (error) {
      console.error('Error loading general settings:', error);
      throw error;
    }
  }

  static async updateGeneralSettings(settings: Partial<GeneralSettings>): Promise<void> {
    try {
      const docRef = doc(db, SETTINGS_COLLECTION, GENERAL_DOC);
      await setDoc(docRef, settings, { merge: true });
    } catch (error) {
      console.error('Error updating general settings:', error);
      throw error;
    }
  }

  /** `onProgress` receives a percentage, 0-100. */
  static async uploadIntroVideo(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    try {
      const fileName = `intro_video_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `videos/${fileName}`);

      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(Math.round(progress));
            }
          },
          (error) => {
            console.error('Error uploading video:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error uploading intro video:', error);
      throw error;
    }
  }

  static async updateIntroVideo(videoUrl: string): Promise<void> {
    try {
      await this.updateGeneralSettings({ intro_video: videoUrl });
    } catch (error) {
      console.error('Error updating intro video:', error);
      throw error;
    }
  }

  static async updateDefaultCoach(coachId: string): Promise<void> {
    try {
      await this.updateGeneralSettings({ default_coach: coachId });
    } catch (error) {
      console.error('Error updating default coach:', error);
      throw error;
    }
  }
}
